/* Modal History Manager (window.DDModalHistory)
   ------------------------------------------------------------------
   Invisible history layer that makes the hardware / browser Back
   button close the top-most open overlay (drawer, modal, lightbox,
   bottom sheet) instead of leaving the page.

   How it works
   ------------
   - An overlay registers itself once (notifyOpen) right after its
     open commits and hands over its OWN close function, so a Back
     press runs the exact same close path the UI uses.
   - Opening pushes one same-URL dummy history entry ({ __ddm, d })
     per open overlay. No hash routing, no reloads.
   - Backing out of a dummy runs the registered close function for
     that instance via popstate. Closing through the UI pops the
     stack entry and steps back over its dummy, so the user always
     stays anchored on the page entry - the next Back exits the page
     exactly as it always did.
   - Forward traversal into a dummy never reopens anything: it is
     bounced straight back so a parked user can still exit with a
     single Back press.
   - Every history operation is serialized (queued until the pop in
     flight settles), so rapid multi-clicking can never strand dummy
     entries.
   - Identity is the overlay element itself (the key), which keeps
     any number of instances of the same overlay type distinct.

   API
   ---
   notifyOpen(key, closeFn)  - overlay opened; closeFn() runs on Back
   notifyClose(key)          - overlay closed through the UI

   Guardrail: this file only calls the existing open/close functions.
   It never touches CSS, markup, animations or timers. */
(function () {
  'use strict';

  if (window.DDModalHistory) return;

  var NONCE = 'ddm' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
  var PEND_TTL = 1500;   /* ms a programmatic pop remains attributable to us while it settles */

  var stack = [];        /* [{ key, close, noHistory, op }] - LIFO order */
  var depth = 0;         /* our dummy entries above the page entry, at the current position */
  var pending = [];      /* timestamps of the history.back() calls we initiated */
  var ops = [];          /* deferred history operations while a pop is settling */
  var sweepTimer = null;

  /* ---------- history state ---------- */

  function isOurs(state) {
    return !!(state && typeof state === 'object' && state.__ddm === NONCE && typeof state.d === 'number');
  }

  /* A reload can land on one of our dummy entries. Strip the marker
     so the fresh page starts from a clean base entry (same-URL
     entries behind it are stepped through silently). */
  function stripMarker() {
    var state = history.state;
    if (!state || typeof state !== 'object' || !state.__ddm) return;
    var clean = null;
    for (var k in state) {
      if (k === '__ddm') continue;
      if (!clean) clean = {};
      clean[k] = state[k];
    }
    try { history.replaceState(clean, '', location.href); } catch (e) {}
  }

  /* ---------- pending-pop bookkeeping ---------- */

  function stampPending() {
    pending.push(Date.now());
    if (!sweepTimer) sweepTimer = setTimeout(sweep, PEND_TTL);
  }

  function consumePending() {
    pending.shift();
  }

  /* Drop pops that never landed (browser ignored history.back()) so
     they can never swallow a real user press later. */
  function sweep() {
    sweepTimer = null;
    var now = Date.now();
    var kept = [];
    for (var i = 0; i < pending.length; i++) {
      if (now - pending[i] < PEND_TTL) kept.push(pending[i]);
    }
    pending = kept;
    drain();
    if (pending.length && !sweepTimer) sweepTimer = setTimeout(sweep, PEND_TTL);
  }

  /* ---------- serialized history operations ---------- */

  function drain() {
    if (pending.length) return;            /* wait until the in-flight pop settles */
    while (ops.length && !pending.length) {
      var op = ops.shift();
      try { op(); } catch (e) {}
    }
  }

  function cancelOp(entry) {
    if (!entry.op) return;
    var i = ops.indexOf(entry.op);
    if (i !== -1) ops.splice(i, 1);
    entry.op = null;
  }

  function pushEntryNow(entry) {
    try {
      history.pushState({ __ddm: NONCE, d: depth + 1 }, '', location.href);
      depth += 1;
      entry.noHistory = false;
    } catch (e) {
      entry.noHistory = true;              /* no dummy entry: still opens, just not Back-closable */
    }
  }

  function queuePush(entry) {
    entry.op = function () {
      entry.op = null;
      pushEntryNow(entry);
    };
    ops.push(entry.op);
  }

  function backNow() {
    stampPending();
    try { history.back(); } catch (e) { pending.pop(); }
  }

  function queueBack(entry) {
    entry.op = function () {
      entry.op = null;
      backNow();
    };
    ops.push(entry.op);
  }

  function busy() {
    return pending.length > 0 || ops.length > 0;
  }

  function indexOfKey(key) {
    for (var i = 0; i < stack.length; i++) {
      if (stack[i].key === key) return i;
    }
    return -1;
  }

  /* ---------- public API ---------- */

  function notifyOpen(key, closeFn) {
    try {
      if (!key) return;
      if (indexOfKey(key) !== -1) return;  /* already tracked (multi-click / duplicate call) */

      var entry = { key: key, close: closeFn, noHistory: false, op: null };
      stack.push(entry);

      if (busy()) {
        /* A pop we initiated is still settling: defer the push so the
           two traversals can never interleave. */
        entry.noHistory = true;
        queuePush(entry);
      } else {
        pushEntryNow(entry);
      }
    } catch (e) { /* never break the page */ }
  }

  function notifyClose(key) {
    try {
      var i = indexOfKey(key);
      if (i === -1) return;                /* not tracked (Back path already popped it) */

      var entry = stack[i];
      var wasTop = (i === stack.length - 1);
      stack.splice(i, 1);

      /* Its dummy is still queued, so nothing sits on the history
         list for it: simply drop the queued push. */
      if (entry.op) {
        cancelOp(entry);
        return;
      }

      /* Only the top overlay steps history back; middle entries just
         come off the stack (an orphan dummy is consumed silently). */
      if (!wasTop || entry.noHistory || depth < 1) return;

      if (busy()) {
        queueBack(entry);
      } else {
        backNow();
      }
    } catch (e) { /* never break the page */ }
  }

  /* ---------- popstate reconciliation ---------- */

  function onPop(event) {
    try {
      var landedOurs = isOurs(event.state);
      var landed = landedOurs ? event.state.d : 0;
      var delta = depth - landed;          /* > 0: moved back - < 0: moved forward */

      if (delta > 0) {
        /* Backward: pops we initiated are explained first ... */
        while (delta > 0 && pending.length) {
          consumePending();
          depth -= 1;
          delta -= 1;
        }
        /* ... any leftover steps are real user presses: close the
           top overlays, exactly one per step. */
        while (delta > 0) {
          delta -= 1;
          depth -= 1;
          var entry = stack.pop();
          if (!entry) continue;
          if (entry.op) cancelOp(entry);
          if (typeof entry.close === 'function') {
            try { entry.close(); } catch (e) {}
          }
        }
        depth = landed > 0 ? landed : 0;
      } else if (delta < 0) {
        /* Forward traversal: never reopen. Bounce straight back so
           the user stays anchored and a single Back still exits. */
        if (landedOurs) {
          depth = landed;
          backNow();
        }
      } else if (pending.length) {
        consumePending();                  /* a settled pop that moved nothing we track */
      }

      drain();
    } catch (e) { /* never break the page */ }
  }

  /* ---------- init ---------- */

  stripMarker();

  window.DDModalHistory = {
    notifyOpen: notifyOpen,
    notifyClose: notifyClose
  };

  window.addEventListener('popstate', onPop);
})();
